// src/components/ThemeToggle.tsx
//
// The light/dark switch. One button, rendered by whichever surface wants it;
// the Navbar is currently the only one.
//
// It reads the mode from ThemeContext and calls the context's toggleTheme. It
// holds no theme state of its own and knows no colours beyond the tokens it
// renders, so a change to styles/theme.ts is the only thing needed to restyle it.
//
// On the icon: it shows the theme the visitor would move TO, not the one they
// are in -- a sun while the site is dark, a moon while it is light. That is the
// same convention as an OS appearance switch, and it means the icon and the
// label below always agree, because both are derived from the same value.
import styled, { css } from 'styled-components';
import { Sun, Moon } from 'lucide-react';

import { useTheme } from '../context/ThemeContext';

/**
 * The themed two-tone focus ring, in a `css` helper rather than inline.
 *
 * Two reasons, both matching Navbar.styles.ts and Footer.styles.ts. First,
 * `&&` reaches (0,3,0) and beats the fixed grey ring globalStyles.ts puts on
 * `button:focus-visible` at (0,2,1). Second, and the reason this is not written
 * inline below: TypeScript cannot infer the `theme` argument for an
 * interpolation nested inside a `&&` block written directly in a
 * `styled.button` template, and reports it as an implicit `any`. Extracting it
 * to a `css` helper types it correctly.
 */
const focusRing = css`
  &&:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.accent};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.background},
      0 0 0 4px ${({ theme }) => theme.accent};
  }
`;

const Button = styled.button`
  /* A filled circle, which is what theme.toggleBg exists for. Kept flat: the
     site has no bouncing motion anywhere, so a scale-on-hover would be the one
     control that breaks that rule. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  padding: 0;
  background: ${({ theme }) => theme.toggleBg};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease,
    border-color 0.2s ease;

  svg {
    display: block;
  }

  &:hover {
    color: ${({ theme }) => theme.text};
    border-color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.cardBg};

    /* globalStyles.ts lifts and shadows every button on hover. These have to
       live inside a :hover rule to be worth anything: .class:hover is (0,2,0)
       and outranks the global (0,1,1), whereas a bare .class would lose. */
    transform: none;
    box-shadow: none;
  }

  ${focusRing}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ThemeToggle = () => {
  const { theme, toggleTheme } = useTheme();

  // The label states the action, not the state, so it is correct for someone
  // who cannot see the icon. `title` gives the same words to a mouse user.
  const nextTheme = theme === 'light' ? 'dark' : 'light';
  const label = `Switch to ${nextTheme} theme`;

  return (
    <Button type="button" onClick={toggleTheme} aria-label={label} title={label}>
      {theme === 'light' ? (
        <Moon size={18} strokeWidth={1.75} aria-hidden="true" />
      ) : (
        <Sun size={18} strokeWidth={1.75} aria-hidden="true" />
      )}
    </Button>
  );
};

export default ThemeToggle;
